import React from 'react';
import { Activity, Award, BarChart3, Brain, ChevronRight, Heart, Loader2, MapPin, Users, Zap } from 'lucide-react';
import { StatCard } from '../SharedComponents';

const RollupTab = ({ ctx }) => {
  const {  members, analytics, rollupData, API_URL, getToken , scopedAnalytics, selectedLocationId, setScopedAnalytics, setSelectedLocationId } = ctx;

  return (
<div data-testid="rollup-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Location Roll-Up</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Head-office view — compare all branch locations side-by-side. Click any location to drill into its scoped analytics.
  </p>

  {rollupData?.demo_data && (
    <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium mb-4" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}>
      <BarChart3 size={14} />
      Sample data — will reflect real branch activity once sub-admins and members are assigned to locations
    </div>
  )}

  {rollupData && (
    <>
      {/* Org totals */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total Members" value={rollupData.totals?.total_members} icon={<Users size={16} style={{ color: '#3B82F6' }} />} />
        <StatCard label="Avg Engagement" value={`${rollupData.totals?.avg_active_rate}%`} icon={<Activity size={16} style={{ color: '#F5831F' }} />} />
        <StatCard label="Avg Pulse" value={`${rollupData.totals?.avg_pulse}/5`} icon={<Heart size={16} style={{ color: '#EC4899' }} />} />
        <StatCard label="Avg Sessions/Wk" value={rollupData.totals?.avg_sessions} icon={<Zap size={16} style={{ color: '#059669' }} />} />
        <StatCard label="Total Badges" value={rollupData.totals?.total_badges} icon={<Award size={16} style={{ color: '#F59E0B' }} />} />
      </div>

      {/* Location comparison table */}
      <div className="bg-white rounded-xl overflow-hidden mb-6" style={{ border: '1px solid #E8E8E8' }} data-testid="rollup-table">
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: '#F8F7F4', borderBottom: '1px solid #E8E8E8' }}>
              {['Location', 'Members', 'Active %', 'Sessions/Wk', 'Pulse', 'OCEAN %', 'Sentiment', 'Badges', 'Top Dept', ''].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#999' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rollupData.locations?.map((loc, i) => {
              const isSelected = selectedLocationId === loc.location_id;
              return (
                <tr key={loc.location_id} className="hover:bg-gray-50 cursor-pointer transition-colors"
                  style={{ borderBottom: '1px solid #F0F0F0', backgroundColor: isSelected ? '#F5831F08' : loc.is_hq ? '#FAFAF8' : undefined }}
                  onClick={async () => {
                    setSelectedLocationId(loc.location_id);
                    try {
                      const res = await fetch(`${API_URL}/api/corporate/sub-admin/scoped-analytics?location_id=${loc.location_id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
                      if (res.ok) setScopedAnalytics(await res.json());
                    } catch {}
                  }}
                  data-testid={`rollup-row-${i}`}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <MapPin size={12} style={{ color: loc.is_hq ? '#F5831F' : '#9CA3AF' }} />
                      <div>
                        <p className="text-xs font-bold" style={{ color: '#1A1209' }}>{loc.name}</p>
                        <p className="text-[9px]" style={{ color: '#BBB' }}>{loc.city}, {loc.state}</p>
                      </div>
                      {loc.is_hq && <span className="px-1.5 py-0.5 rounded text-[7px] font-bold" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>HQ</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs font-semibold" style={{ color: '#1A1209' }}>{loc.members}</td>
                  <td className="px-3 py-3">
                    <span className="text-xs font-bold" style={{ color: loc.active_rate >= 70 ? '#059669' : loc.active_rate >= 50 ? '#F59E0B' : '#DC2626' }}>{loc.active_rate}%</span>
                  </td>
                  <td className="px-3 py-3 text-xs" style={{ color: '#666' }}>{loc.sessions_per_member}</td>
                  <td className="px-3 py-3">
                    <span className="text-xs font-bold" style={{ color: loc.pulse_score >= 4 ? '#059669' : loc.pulse_score >= 3.5 ? '#F59E0B' : '#DC2626' }}>{loc.pulse_score}</span>
                  </td>
                  <td className="px-3 py-3 text-xs" style={{ color: '#666' }}>{loc.ocean_completion}%</td>
                  <td className="px-3 py-3 text-xs" style={{ color: '#666' }}>{loc.sentiment}</td>
                  <td className="px-3 py-3 text-xs" style={{ color: '#666' }}>{loc.badges_earned}</td>
                  <td className="px-3 py-3">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#F8F7F4', color: '#666' }}>{loc.top_department}</span>
                  </td>
                  <td className="px-3 py-3">
                    <ChevronRight size={12} style={{ color: isSelected ? '#F5831F' : '#DDD' }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Scoped drill-down */}
      {scopedAnalytics && selectedLocationId && (
        <div className="bg-white rounded-xl p-5" style={{ border: '2px solid #F5831F22' }} data-testid="scoped-drilldown">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
                <MapPin size={14} style={{ color: '#F5831F' }} />
                {scopedAnalytics.location_name} — Detailed Analytics
              </h3>
              <p className="text-[10px]" style={{ color: '#999' }}>Scoped view of this location&apos;s performance</p>
            </div>
            <button onClick={() => { setSelectedLocationId(null); setScopedAnalytics(null); }}
              className="px-3 py-1.5 rounded-lg text-xs" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Close</button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard label="Members" value={scopedAnalytics.members} icon={<Users size={14} style={{ color: '#3B82F6' }} />} />
            <StatCard label="Engagement" value={`${scopedAnalytics.engagement_rate}%`} icon={<Activity size={14} style={{ color: '#F5831F' }} />} />
            <StatCard label="Pulse" value={`${scopedAnalytics.pulse_score}/5`} icon={<Heart size={14} style={{ color: '#EC4899' }} />} />
            <StatCard label="OCEAN Done" value={`${scopedAnalytics.ocean_completion}%`} icon={<Brain size={14} style={{ color: '#8B5CF6' }} />} />
          </div>

          {scopedAnalytics.departments?.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#999' }}>Department Breakdown</h4>
              <div className="space-y-2">
                {scopedAnalytics.departments.map(d => (
                  <div key={d.department} className="flex items-center gap-4 px-4 py-2.5 rounded-lg" style={{ backgroundColor: '#F8F7F4' }}>
                    <span className="text-xs font-bold w-28" style={{ color: '#1A1209' }}>{d.department}</span>
                    <span className="text-[10px] w-16" style={{ color: '#666' }}>{d.members} ppl</span>
                    <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: '#E8E8E8' }}>
                      <div className="h-full rounded-full" style={{ width: `${d.active_rate}%`, backgroundColor: d.active_rate >= 70 ? '#10B981' : d.active_rate >= 50 ? '#F59E0B' : '#EF4444' }} />
                    </div>
                    <span className="text-[10px] font-bold w-12 text-right" style={{ color: d.active_rate >= 70 ? '#059669' : '#F59E0B' }}>{d.active_rate}%</span>
                    <span className="text-[10px] w-10 text-right" style={{ color: '#999' }}>{d.sessions} sess</span>
                    <span className="text-[10px] w-10 text-right" style={{ color: '#999' }}>{d.pulse}/5</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )}

  {!rollupData && (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#DDD' }} />
      <p className="text-sm" style={{ color: '#999' }}>Loading location data...</p>
    </div>
  )}
</div>
  );
};

export default RollupTab;
